import * as React from "react";
import classnames from "classnames";
import { scaleTime, scaleLinear, ScaleTime, ScaleLinear } from "d3-scale";
import { isNumber, assertValue } from "../../app/utils/assert";
import { translate } from "../../d3chart";
import { Axis, AxisType } from "../../d3chart/Axes";
import { Brush, BrushDimension } from "../../d3chart/Brush";
import { getClip, defaultWheelDelta, getPoint, scaleSelection } from "../../d3chart/zoom";
import { extentDataSets, extentDataSet } from "../Utils";
import { Line } from "./Line";
import { SweepLine } from "./SweepLine";
import { IDataSet, TooltipInfo, SequenceState, ISample } from "../Data";
import { TimeSeriesActions } from "./flux/TimeSeriesActions";
import moment from 'moment';

let styles = require('./TimeSeries.local.css');

const severityColors = {
    'red': '#d62728',
    'yellow': '#ffc90e'
}

type Numeric = { valueOf(): number; };
type Domain = number | Numeric;

interface Props
{
    tooltip: TooltipInfo;
    dataSets: IDataSet[];
    zoomedDataSets: IDataSet[];    

    width?: number;
    height?: number;

    onSweepLine: (point: { clientX: number, clientY: number }) => void;
    isRangeOk?: (range: [Date, Date]) => boolean;
}

interface State
{
    // make some padding for our canvas
    leftMargin: number;
    innerHeight: number;
    innerWidth: number;
    
    // coordinates to put Brush component
    brushTop: number;
    brushBaseLine: number;
    
    // maps time range to canvas width
    xScale: ScaleTime<number, number>;
    // maps time range of interest to canvas width
    xZoomedScale: ScaleTime<number, number>;    
    yScales: ScaleLinear<number, number>[];
    yBrushScales: ScaleLinear<number, number>[];
}

// TimeSeries components manages SVG element
// Component holds:
// * A few Axes child components (dedicated X,Y axes for each DataSet)
// * Lines for each sequences
// * Sweep line
// * Brush component
export class TimeSeries extends React.Component<Props & React.HTMLProps<SVGElement>, State>
{
    public static defaultProps: Partial<Props> = {
        width: 900,
        height: 500,
    };

    private readonly actions = new TimeSeriesActions();
    private canvas: SVGGraphicsElement;

    private readonly axisWidth = 60;
    private readonly brushHeight = 100;
    private readonly margin = { left: 20, top: 20, right: 20, bottom: 150 };
    
    constructor(props: Props)
    {
        super(props);
        this.state = this.getStateFor(props);
    }

    // parent element are welcome to update widht and height
    componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any)
    {
        this.setState(this.getStateFor(nextProps));
    }

    onlyUnique(value, index, self) { 
        return self.indexOf(value) === index;
    }


    render()
    {
        const { width, height, tooltip, dataSets, zoomedDataSets } = this.props;
        const {
            leftMargin,
            innerHeight, innerWidth,
            xScale, xZoomedScale,
            yScales, yBrushScales,
            brushTop, brushBaseLine,
        } = this.state;
        assertValue(zoomedDataSets);

        const sweepLine = tooltip ? xZoomedScale(tooltip.x) : null;
        return zoomedDataSets.length ? (
            <svg width={width} height={height} onWheel={this.onWheel} onMouseMove={this.onMouseMove} className={this.props.className}>
                <g transform={translate(leftMargin, this.margin.top)} ref={e => this.canvas = e as SVGGraphicsElement}>
                    { zoomedDataSets.map((ds, idx) => ds.sequences.filter(seq => seq.state !== SequenceState.Hidden).map((seq, sIdx) => <Line key={`seq_${sIdx}`} xScale={xZoomedScale} yScale={yScales[idx]} sequence={seq} />)) }
                    { zoomedDataSets.map((ds, idx) => ds.sequences.filter(seq => seq.state !== SequenceState.Hidden).map((seq, sid) => seq.samples.filter(s => s.severity).map((s,i) => <circle key={`${idx}_${sid}_${i}`} fill={severityColors[s.severity]} r={5} cx={xZoomedScale(s.x)} cy={yScales[idx](s.y)} />))) }
                    <SweepLine tooltip={tooltip} xScale={xZoomedScale} yScales={yScales} height={innerHeight} />
                </g>

                {/* Y axes */}
                { zoomedDataSets.map((ds, idx) => <Axis key={idx} transform={translate(this.axisShift(idx), this.margin.top)} axisType={AxisType.Left} axisScale={yScales[idx]} title={ds.yAxisTitle} />) }
                {/* X axis */}
                <Axis transform={translate(leftMargin, height - this.margin.bottom)} axisType={AxisType.Bottom} axisScale={xZoomedScale} />
                

                {/* Brush */}
                <Axis className={classnames(styles.Axis, styles.AxisGrid)}
                    transform={translate(leftMargin, brushBaseLine)} axisType={AxisType.Bottom} axisScale={xScale}
                    tickFormat={() => null}
                    tickSize={-this.brushHeight}
                />
                <Axis transform={translate(leftMargin, brushBaseLine)} axisType={AxisType.Bottom} axisScale={xScale} />
                
                {/* Lines inside brush */}
                <g transform={translate(leftMargin, brushTop)} className="brush-chart">
                    { dataSets.map((ds, idx) => ds.sequences.map((seq, sIdx) => <Line key={`seq_${sIdx}`} xScale={xScale} yScale={yBrushScales[idx]} sequence={seq} />)) }
                </g>
                <Brush dimension={BrushDimension.X}
                    transform={translate(leftMargin, brushTop)}
                    x0={0} y0={0} x1={innerWidth} y1={this.brushHeight}
                    w={xScale(xZoomedScale.domain()[0])} e={xScale(xZoomedScale.domain()[1])}
                    onBrush={this.onBrush}
                />
            </svg>
        ) : null;
    }

    private axisShift = (index: number) => this.margin.left + (index + 1) * this.axisWidth;

    private onBrush = (w: number, n: number, e: number, s: number) => {
        const { xScale } = this.state;
        const range: [Date, Date] = [xScale.invert(w), xScale.invert(e)];
        this.trySetRange(range);
    }

    // Handle mouse wheel event
    // Calculates new time range of interest and updates the state
    private onWheel = (e: React.WheelEvent<SVGElement>) => {
        if(e.altKey) return;
        
        const { dataSets, zoomedDataSets } = this.props;
        const { xZoomedScale } = this.state;
        const pivot = xZoomedScale.invert(getPoint(e, this.canvas).x);
        const delta = defaultWheelDelta(e);
        
        const extent: [Date, Date] = extentDataSets(dataSets, s => s.x)
        const selection: [Date, Date] = extentDataSets(zoomedDataSets, s => s.x);

        const range = scaleSelection<Date>(extent, selection, pivot, delta);
        this.trySetRange(range);
    }

    private trySetRange = (range: [Date, Date]) => {
        if(this.isRangeOk(range)) {
            this.actions.setRange(range);
        }
    }

    // Handle mouse move event
    // Update the sweep line position
    private onMouseMove = (e: React.MouseEvent<SVGElement>) => {
        if(!this.canvas || e.altKey) return;
        const { innerHeight, innerWidth, xZoomedScale } = this.state;
        const p = getPoint(e, this.canvas);
        const { clientX, clientY } = e;
        const visible = (p.x >= 0 && p.x <= innerWidth) && (p.y >= 0 && p.y <= innerHeight);
        const t = visible ? xZoomedScale.invert(p.x) : null;
        
        this.actions.setTooltipTime(t);
        
        const { onSweepLine } = this.props;
        if(onSweepLine) {
            onSweepLine({ clientX, clientY });
        }
    }

    private isRangeOk = (range: [Date, Date]) => {
        const { isRangeOk: isOk } = this.props;
        return isOk ? isOk(range) : true;
    }

    // convert component's properties into internal state
    private getStateFor = <K extends keyof State>(props: Readonly<Props>) => {
        const { dataSets: allDataSets, zoomedDataSets } = props;
        const yAxesCnt = allDataSets.length;
        const innerWidth = props.width - this.margin.left - this.margin.right - this.axisWidth * yAxesCnt;
        const innerHeight = props.height - this.margin.top - this.margin.bottom;

        const getXScaleFor = (dataSets: IDataSet[]) => {
            const xDomain = extentDataSets(dataSets, s => s.x);
            return scaleTime().rangeRound([0, innerWidth]).domain(xDomain);
        }

        const yScaleFor = (dataSet: IDataSet, height: number) => {
            const yDomain = extentDataSet(dataSet, s => s.y);
            if(isNumber(dataSet.minValue)) {
                yDomain[0] = Math.min(dataSet.minValue, yDomain[0]);
            }
            
            return scaleLinear().domain(yDomain).rangeRound([height, 0]);
        }
        const brushBaseLine = props.height - 20; 
        const brushTop = brushBaseLine - this.brushHeight;
        const yScales = allDataSets.map(ds => yScaleFor(ds, innerHeight));
        const yBrushScales = allDataSets.map(ds => yScaleFor(ds, this.brushHeight));
    
        return {
            leftMargin: this.axisShift(allDataSets.length - 1),
            innerHeight,
            innerWidth,
            xScale: getXScaleFor(allDataSets),
            xZoomedScale: getXScaleFor(zoomedDataSets),
            yScales,
            yBrushScales,
            brushTop,
            brushBaseLine,
        };
    }
}


// WEBPACK FOOTER //
// ./src/charts/TimeSeries/TimeSeries.tsx